import { useState } from "react";
import { motion } from "framer-motion";
import { Download, CheckCircle2, Clock, FileText, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuidePage() {
  const [formData, setFormData] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);

  const handleDownload = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.name) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/download-guide`, formData, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', '1031_Exchange_Guide.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Guide downloaded! Check your email for more resources.");
      setFormData({ name: "", email: "" });
    } catch (err) {
      toast.error("Failed to download guide. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="overline mb-4 block">Free Buyer's Guide</span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#1A1A2E] leading-[1.1] mb-6" data-testid="guide-title">
                The Buyer's Guide to 1031 Exchange Condo Units
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8">
                A comprehensive guide explaining how buyers use condo units to complete successful 1031 exchanges.
              </p>

              {/* Download Form */}
              <form onSubmit={handleDownload} className="space-y-4 max-w-md">
                <Input
                  type="text"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-14 px-6 text-base rounded-sm border-slate-200"
                  data-testid="guide-name-input"
                />
                <Input
                  type="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-14 px-6 text-base rounded-sm border-slate-200"
                  data-testid="guide-email-input"
                />
                <Button 
                  type="submit" 
                  className="btn-primary w-full h-14 text-base rounded-sm"
                  disabled={loading}
                  data-testid="guide-download-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner" />
                      Generating PDF...
                    </span>
                  ) : (
                    <>
                      <Download className="mr-2 w-5 h-5" />
                      Download Free Guide
                    </>
                  )}
                </Button>
              </form>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-white p-8 shadow-float border border-slate-100">
                <h3 className="text-xl font-medium text-[#1A1A2E] mb-6">What You'll Learn:</h3>
                <ul className="space-y-4">
                  {[
                    "How 1031 exchanges work",
                    "Key timelines and deadlines",
                    "Common mistakes to avoid",
                    "Why condos work for exchanges",
                    "NYC and Miami market insights",
                    "Timeline strategies for success"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-slate-600">
                      <CheckCircle2 className="w-5 h-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content Overview */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="overline mb-4 block">Understanding 1031 Exchanges</span>
            <h2 className="text-4xl md:text-5xl font-light tracking-tight text-[#1A1A2E] leading-tight">
              What is a 1031 Exchange?
            </h2>
          </motion.div>

          <div className="prose-custom max-w-4xl mx-auto">
            <p>
              A 1031 exchange, named after Section 1031 of the Internal Revenue Code, is one of the most widely used tax-deferral provisions available to real estate owners. Under this provision, property owners may defer capital gains taxes when selling a qualifying property and reinvesting the proceeds into another qualifying property of like kind.
            </p>
            
            <p>
              This provision allows property owners to preserve more capital, reposition their real estate holdings, transition into different geographic markets, and continue compounding long-term value without immediate tax liability.
            </p>

            <h2>Key Timelines</h2>
            <div className="grid md:grid-cols-2 gap-6 my-8 not-prose">
              <div className="p-6 bg-slate-50 border border-slate-100">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-6 h-6 text-[#0066FF]" />
                  <span className="text-2xl font-bold text-[#1A1A2E]">45 Days</span>
                </div>
                <h4 className="font-medium text-[#1A1A2E] mb-2">Identification Period</h4>
                <p className="text-slate-600 text-sm">From the date you sell your property, you have exactly 45 days to identify potential replacement properties in writing.</p>
              </div>
              <div className="p-6 bg-slate-50 border border-slate-100">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-6 h-6 text-[#0066FF]" />
                  <span className="text-2xl font-bold text-[#1A1A2E]">180 Days</span>
                </div>
                <h4 className="font-medium text-[#1A1A2E] mb-2">Exchange Period</h4>
                <p className="text-slate-600 text-sm">You must close on your replacement property within 180 days of selling your original property.</p>
              </div>
            </div>

            <h2>Why Condo Units?</h2>
            <p>
              Many buyers completing a 1031 exchange seek to transition from actively managed real estate assets into individual condo units that require less operational involvement. Luxury condo units offer several advantages:
            </p>
            <ul>
              <li><strong>Simplified Ownership:</strong> Hold direct title while benefiting from shared building management</li>
              <li><strong>Lower Complexity:</strong> Reduced operational requirements relative to managing entire buildings</li>
              <li><strong>Institutional Quality:</strong> Luxury developments with high-end amenities and professional management</li>
              <li><strong>Strong Liquidity:</strong> Broader resale markets than many commercial properties</li>
            </ul>

            <h2>Common Mistakes to Avoid</h2>
            <ul>
              <li>Missing the 45-day identification deadline</li>
              <li>Failing to use a qualified intermediary</li>
              <li>Taking constructive receipt of funds</li>
              <li>Not identifying enough replacement properties</li>
              <li>Underestimating closing costs and transaction fees</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#1A1A2E]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Shield className="w-12 h-12 text-[#0066FF] mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
              Ready to Start Your 1031 Exchange?
            </h2>
            <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
              Our team can help you identify and review individual condo units that may align with your 1031 exchange requirements and timeline. Different property types may be considered in a 1031 exchange depending on a buyer's objectives, preferences, management expectations, and transaction criteria.
            </p>
            <Button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-white text-[#1A1A2E] hover:bg-slate-100 px-8 py-6 text-base rounded-sm"
            >
              Download the Full Guide
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
